import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface UnbilledUsageItem {
  customerId: string
  customerName: string
  subscriptionId: string
  subscriptionName: string
  productName: string
  meterCategory: string
  meterSubCategory: string
  resourceGroup: string
  resourceLocation: string
  chargeType: string
  quantity: number
  unitPrice: number
  totalAmount: number
  currency: string
  chargeStartDate: string
  chargeEndDate: string
  billingCycleType: string
}

interface ServiceSummary {
  serviceName: string
  totalAmount: number
  itemCount: number
  subscriptions: Set<string>
}

interface SubscriptionSummary {
  subscriptionName: string
  subscriptionId: string
  totalAmount: number
  itemCount: number
  services: { [key: string]: number }
}

interface BilledUsageItem {
  invoiceId: string
  customerId: string
  customerName: string
  subscriptionId: string
  subscriptionName?: string
  productName: string
  meterCategory?: string
  meterSubCategory?: string
  resourceGroup?: string
  resourceLocation?: string
  chargeType: string
  quantity: number
  unitPrice: number
  totalAmount: number
  currency: string
  chargeStartDate: string
  chargeEndDate: string
}

interface Invoice {
  id: string
  invoiceDate: string
  billingPeriodStartDate: string
  billingPeriodEndDate: string
  totalCharges: number
  currencyCode: string
  invoiceType: string
}

interface AzureBillingData {
  unbilled?: {
    items: UnbilledUsageItem[]
    totalAmount: number
    periodStart: string
    periodEnd: string
    lastSyncedAt?: string
    isStale?: boolean
  }
  billed?: {
    items: BilledUsageItem[]
    totalAmount: number
    invoiceId: string
    invoiceDate: string
    billingPeriodStart?: string
    billingPeriodEnd?: string
  }
  availableInvoices?: Invoice[]
}

interface Customer {
  id: string
  name: string
  domain?: string
  hasAzureSubscription: boolean
}

export default function CustomerAzureBilling() {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<AzureBillingData | null>(null)
  const [expandedSection, setExpandedSection] = useState<'unbilled' | 'billed' | 'both'>('unbilled')
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [viewMode, setViewMode] = useState<'summary' | 'subscription' | 'details'>('summary')
  const [expandedService, setExpandedService] = useState<string | null>(null)
  const [showAllServices, setShowAllServices] = useState(false)
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null)
  const [loadingBilled, setLoadingBilled] = useState(false)
  const [billedViewMode, setBilledViewMode] = useState<'summary' | 'subscription' | 'details'>('summary')
  const [expandedBilledService, setExpandedBilledService] = useState<string | null>(null)
  const [showAllBilledServices, setShowAllBilledServices] = useState(false)
  const [showAllBilledSubscriptions, setShowAllBilledSubscriptions] = useState(false)
  const MAX_VISIBLE_ITEMS = 6

  useEffect(() => {
    loadData()
  }, [customerId])

  // Load billed data for a specific invoice
  const loadBilledData = async (invoiceId: string) => {
    if (!customerId) return

    try {
      setLoadingBilled(true)
      const token = await getAccessToken()

      // Get customer info to find Partner Center ID
      const customerData = await api.getSetupCustomer(token, customerId)
      const pcMapping = customerData.customer?.mappings?.find(
        (m: { integrationId: string }) => m.integrationId === 'partner-center'
      )

      if (pcMapping) {
        const billingData = await api.getAzureBilling(token, {
          view: 'billed',
          customerId: pcMapping.externalId,
          invoiceId,
        })

        // Update only the billed portion of the data
        setData(prev => ({
          ...prev,
          billed: billingData.billed,
          availableInvoices: billingData.availableInvoices || prev?.availableInvoices,
        }))
        setSelectedInvoiceId(invoiceId)
      }
    } catch (err) {
      console.error('Failed to load billed data:', err)
      setSyncMessage({
        type: 'error',
        text: `Failed to load billed data for invoice ${invoiceId}`,
      })
    } finally {
      setLoadingBilled(false)
    }
  }

  const loadData = async () => {
    if (!customerId) return

    try {
      setLoading(true)
      setError(null)
      const token = await getAccessToken()

      // Fetch customer info first
      const customerData = await api.getSetupCustomer(token, customerId)
      if (!customerData.customer) {
        setError('Customer not found')
        return
      }
      setCustomer(customerData.customer)

      // Get Partner Center ID from mappings
      const pcMapping = customerData.customer.mappings?.find(
        (m: { integrationId: string }) => m.integrationId === 'partner-center'
      )

      if (pcMapping) {
        // Fetch Azure billing data for this customer (both unbilled and billed)
        // Unbilled data now comes from database (synced periodically), not live API
        const billingData = await api.getAzureBilling(token, {
          view: 'both',
          customerId: pcMapping.externalId,
        })
        setData(billingData)
        // Set default invoice selection
        if (billingData.availableInvoices && billingData.availableInvoices.length > 0) {
          setSelectedInvoiceId(billingData.billed?.invoiceId || billingData.availableInvoices[0].id)
        }
      } else {
        setData({ unbilled: { items: [], totalAmount: 0, periodStart: '', periodEnd: '' }, billed: { items: [], totalAmount: 0, invoiceId: '', invoiceDate: '' } })
      }
    } catch (err) {
      console.error('Failed to fetch Azure billing:', err)
      setError('Failed to load Azure billing data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const syncBillingData = async () => {
    try {
      setSyncing(true)
      setSyncMessage(null)
      const token = await getAccessToken()

      const result = await api.syncBillingData(token, { monthsBack: 3 })

      if (result.success) {
        setSyncMessage({
          type: 'success',
          text: `Synced ${result.lineItemsCount} line items from ${result.invoicesProcessed} invoice(s).`,
        })
        // Reload data after sync
        await loadData()
      } else {
        setSyncMessage({
          type: 'error',
          text: result.error || 'Sync failed',
        })
      }
    } catch (err) {
      console.error('Billing sync failed:', err)
      setSyncMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to sync billing data',
      })
    } finally {
      setSyncing(false)
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatRelativeTime = (dateString: string | undefined) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString()
  }

  // Summarize items by meter category (from Azure API)
  const getServiceSummary = (items: UnbilledUsageItem[]): ServiceSummary[] => {
    const summaryMap = new Map<string, ServiceSummary>()

    items.forEach(item => {
      const service = item.meterCategory || 'Other'
      const existing = summaryMap.get(service)

      if (existing) {
        existing.totalAmount += item.totalAmount
        existing.itemCount += 1
        existing.subscriptions.add(item.subscriptionName)
      } else {
        summaryMap.set(service, {
          serviceName: service,
          totalAmount: item.totalAmount,
          itemCount: 1,
          subscriptions: new Set([item.subscriptionName]),
        })
      }
    })

    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Summarize items by subscription
  const getSubscriptionSummary = (items: UnbilledUsageItem[]): SubscriptionSummary[] => {
    const summaryMap = new Map<string, SubscriptionSummary>()

    items.forEach(item => {
      const key = item.subscriptionId
      const existing = summaryMap.get(key)
      const service = item.meterCategory || 'Other'

      if (existing) {
        existing.totalAmount += item.totalAmount
        existing.itemCount += 1
        existing.services[service] = (existing.services[service] || 0) + item.totalAmount
      } else {
        summaryMap.set(key, {
          subscriptionName: item.subscriptionName,
          subscriptionId: item.subscriptionId,
          totalAmount: item.totalAmount,
          itemCount: 1,
          services: { [service]: item.totalAmount },
        })
      }
    })

    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Get items filtered by meter category
  const getItemsByService = (items: UnbilledUsageItem[], service: string): UnbilledUsageItem[] => {
    return items
      .filter(item => (item.meterCategory || 'Other') === service)
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Summarize billed items by meter category
  const getBilledServiceSummary = (items: BilledUsageItem[]): ServiceSummary[] => {
    const summaryMap = new Map<string, ServiceSummary>()

    items.forEach(item => {
      const service = item.meterCategory || 'Other'
      const existing = summaryMap.get(service)

      if (existing) {
        existing.totalAmount += item.totalAmount
        existing.itemCount += 1
        existing.subscriptions.add(item.subscriptionName || item.subscriptionId)
      } else {
        summaryMap.set(service, {
          serviceName: service,
          totalAmount: item.totalAmount,
          itemCount: 1,
          subscriptions: new Set([item.subscriptionName || item.subscriptionId]),
        })
      }
    })

    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Summarize billed items by subscription
  const getBilledSubscriptionSummary = (items: BilledUsageItem[]): SubscriptionSummary[] => {
    const summaryMap = new Map<string, SubscriptionSummary>()

    items.forEach(item => {
      const key = item.subscriptionId
      const existing = summaryMap.get(key)
      const service = item.meterCategory || 'Other'

      if (existing) {
        existing.totalAmount += item.totalAmount
        existing.itemCount += 1
        existing.services[service] = (existing.services[service] || 0) + item.totalAmount
      } else {
        summaryMap.set(key, {
          subscriptionName: item.subscriptionName || item.subscriptionId,
          subscriptionId: item.subscriptionId,
          totalAmount: item.totalAmount,
          itemCount: 1,
          services: { [service]: item.totalAmount },
        })
      }
    })

    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Get billed items filtered by meter category
  const getBilledItemsByService = (items: BilledUsageItem[], service: string): BilledUsageItem[] => {
    return items
      .filter(item => (item.meterCategory || 'Other') === service)
      .sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Get service icon color based on Azure MeterCategory
  const getServiceColor = (service: string): string => {
    const colors: { [key: string]: string } = {
      // Azure MeterCategory names
      'Storage': 'bg-green-500',
      'Virtual Machines': 'bg-blue-500',
      'Compute': 'bg-blue-500',
      'Networking': 'bg-indigo-500',
      'Virtual Network': 'bg-indigo-500',
      'Bandwidth': 'bg-purple-500',
      'Azure App Service': 'bg-pink-500',
      'Azure Cosmos DB': 'bg-yellow-500',
      'SQL Database': 'bg-yellow-500',
      'Azure Database for MySQL': 'bg-yellow-500',
      'Azure Database for PostgreSQL': 'bg-yellow-500',
      'Key Vault': 'bg-red-500',
      'Security Center': 'bg-red-500',
      'Azure Monitor': 'bg-cyan-500',
      'Log Analytics': 'bg-cyan-500',
      'Application Insights': 'bg-cyan-500',
      'Container Instances': 'bg-orange-500',
      'Azure Kubernetes Service': 'bg-orange-500',
      'Backup': 'bg-teal-500',
      'Recovery Services': 'bg-teal-500',
      'Azure Functions': 'bg-amber-500',
      'Logic Apps': 'bg-violet-500',
      'Event Hubs': 'bg-rose-500',
      'Service Bus': 'bg-rose-500',
      'Redis Cache': 'bg-red-400',
      'Content Delivery Network': 'bg-sky-500',
      'Azure DNS': 'bg-slate-500',
      'Load Balancer': 'bg-indigo-400',
      'VPN Gateway': 'bg-indigo-600',
      'Application Gateway': 'bg-indigo-300',
      'Azure Firewall': 'bg-red-600',
      'Data Management': 'bg-emerald-500',
    }
    return colors[service] || 'bg-gray-500'
  }

  // Get hex color for SVG charts - matches Azure MeterCategory names
  const getServiceHexColor = (service: string): string => {
    const colors: { [key: string]: string } = {
      // Azure MeterCategory names
      'Storage': '#22c55e',
      'Virtual Machines': '#3b82f6',
      'Compute': '#3b82f6',
      'Networking': '#6366f1',
      'Virtual Network': '#6366f1',
      'Bandwidth': '#a855f7',
      'Azure App Service': '#ec4899',
      'Azure Cosmos DB': '#eab308',
      'SQL Database': '#eab308',
      'Azure Database for MySQL': '#eab308',
      'Azure Database for PostgreSQL': '#eab308',
      'Key Vault': '#ef4444',
      'Security Center': '#ef4444',
      'Azure Monitor': '#06b6d4',
      'Log Analytics': '#06b6d4',
      'Application Insights': '#06b6d4',
      'Container Instances': '#f97316',
      'Azure Kubernetes Service': '#f97316',
      'Backup': '#14b8a6',
      'Recovery Services': '#14b8a6',
      'Azure Functions': '#f59e0b',
      'Logic Apps': '#8b5cf6',
      'Event Hubs': '#f43f5e',
      'Service Bus': '#f43f5e',
      'Redis Cache': '#f87171',
      'Content Delivery Network': '#0ea5e9',
      'Azure DNS': '#64748b',
      'Load Balancer': '#818cf8',
      'VPN Gateway': '#4f46e5',
      'Application Gateway': '#a5b4fc',
      'Azure Firewall': '#dc2626',
      'Data Management': '#10b981',
    }
    return colors[service] || '#6b7280'
  }

  // Donut Chart Component
  const DonutChart = ({ data, total }: { data: ServiceSummary[], total: number }) => {
    const size = 180
    const strokeWidth = 35
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const center = size / 2

    let cumulativePercent = 0
    const segments = data.slice(0, 8).map((item) => {
      const percent = (item.totalAmount / total) * 100
      const offset = circumference * (1 - cumulativePercent / 100)
      const length = (circumference * percent) / 100
      cumulativePercent += percent
      return {
        ...item,
        percent,
        offset,
        length,
        color: getServiceHexColor(item.serviceName),
      }
    })

    // Calculate "Other" if there are more than 8 categories
    const otherTotal = data.slice(8).reduce((sum, item) => sum + item.totalAmount, 0)
    if (otherTotal > 0) {
      const percent = (otherTotal / total) * 100
      segments.push({
        serviceName: 'Other',
        totalAmount: otherTotal,
        itemCount: data.slice(8).reduce((sum, item) => sum + item.itemCount, 0),
        subscriptions: new Set<string>(),
        percent,
        offset: circumference * (1 - cumulativePercent / 100),
        length: (circumference * percent) / 100,
        color: '#9ca3af',
      })
    }

    return (
      <div className="flex items-center justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          {/* Data segments */}
          {segments.map((segment, index) => (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segment.length} ${circumference - segment.length}`}
              strokeDashoffset={segment.offset}
              transform={`rotate(-90 ${center} ${center})`}
              className="transition-all duration-300"
            />
          ))}
          {/* Center text */}
          <text
            x={center}
            y={center - 8}
            textAnchor="middle"
            className="text-xs fill-gray-500"
          >
            Total
          </text>
          <text
            x={center}
            y={center + 12}
            textAnchor="middle"
            className="text-lg font-semibold fill-gray-900"
          >
            {formatCurrency(total)}
          </text>
        </svg>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading Azure billing data...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="px-4 sm:px-0">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || 'Customer not found'}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/customers')}
                  className="text-sm font-medium text-red-800 hover:text-red-700"
                >
                  Back to Customers
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/customers/${customerId}`)}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 mb-4"
        >
          ← Back to {customer.name}
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Azure Billing</h2>
            <p className="mt-1 text-sm text-gray-500">
              {customer.name} - Unbilled and billed Azure usage
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={syncBillingData}
              disabled={syncing}
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {syncing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Syncing...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Sync from Partner Center
                </>
              )}
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="inline-flex items-center rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 disabled:bg-sky-400 disabled:cursor-not-allowed"
            >
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Sync Status Message */}
      {syncMessage && (
        <div
          className={`mb-6 rounded-md p-4 ${
            syncMessage.type === 'success' ? 'bg-green-50' : 'bg-red-50'
          }`}
        >
          <div className="flex">
            <div className="flex-shrink-0">
              {syncMessage.type === 'success' ? (
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p
                className={`text-sm font-medium ${
                  syncMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {syncMessage.text}
              </p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setSyncMessage(null)}
                className={`inline-flex ${
                  syncMessage.type === 'success'
                    ? 'text-green-500 hover:text-green-600'
                    : 'text-red-500 hover:text-red-600'
                }`}
              >
                <span className="sr-only">Dismiss</span>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
        {/* Unbilled Summary Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg border-l-4 border-orange-500">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-gray-100 rounded-md p-3">
                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Current Unbilled</dt>
                  <dd className="flex items-baseline">
                    <div className="text-lg font-medium text-gray-400">
                      Not available
                    </div>
                  </dd>
                  <dd className="text-xs text-gray-400 mt-1">
                    See global Azure Billing
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Billed Summary Card */}
        <div className="bg-white overflow-hidden shadow rounded-lg border-l-4 border-green-500">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-100 rounded-md p-3">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Last Invoice</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-green-600">
                      {formatCurrency(data?.billed?.totalAmount || 0)}
                    </div>
                  </dd>
                  <dd className="text-xs text-gray-500 mt-1">
                    {data?.billed?.invoiceId
                      ? `Invoice: ${data.billed.invoiceId}${data.billed.invoiceDate ? ` (${new Date(data.billed.invoiceDate).toLocaleDateString()})` : ''}`
                      : 'No invoice data'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Unbilled Usage Section */}
      <div className={`bg-white shadow rounded-lg mb-6 ${!data?.unbilled?.items?.length ? 'opacity-60' : ''}`}>
        <div
          className="px-4 py-5 sm:px-6 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
          onClick={() => setExpandedSection(expandedSection === 'unbilled' ? 'billed' : 'unbilled')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-3 w-3 rounded-full mr-3 ${data?.unbilled?.items?.length ? 'bg-orange-500' : 'bg-gray-400'}`}></div>
              <h3 className={`text-lg font-medium leading-6 ${data?.unbilled?.items?.length ? 'text-gray-900' : 'text-gray-600'}`}>Current Unbilled Usage</h3>
              {data?.unbilled?.items && data.unbilled.items.length > 0 ? (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
                  {formatCurrency(data.unbilled.totalAmount)}
                </span>
              ) : (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  No data
                </span>
              )}
            </div>
            <div className="flex items-center">
              {data?.unbilled?.lastSyncedAt && (
                <span className={`text-sm mr-4 ${data.unbilled.isStale ? 'text-orange-600' : 'text-gray-500'}`}>
                  Synced {formatRelativeTime(data.unbilled.lastSyncedAt)}
                  {data.unbilled.isStale && ' (stale)'}
                </span>
              )}
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedSection === 'unbilled' ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {data?.unbilled?.periodStart && data?.unbilled?.periodEnd
              ? `${formatDate(data.unbilled.periodStart)} - ${formatDate(data.unbilled.periodEnd)}`
              : 'Current billing period usage'}
          </p>
        </div>

        {expandedSection === 'unbilled' && (
          <div>
            {data?.unbilled?.items && data.unbilled.items.length > 0 ? (
              <>
                {/* View Mode Toggle */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">View by:</span>
                    <div className="inline-flex rounded-md shadow-sm">
                      <button
                        type="button"
                        onClick={() => { setViewMode('summary'); setExpandedService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                          viewMode === 'summary'
                            ? 'bg-orange-100 text-orange-700 border-orange-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Service
                      </button>
                      <button
                        type="button"
                        onClick={() => { setViewMode('subscription'); setExpandedService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                          viewMode === 'subscription'
                            ? 'bg-orange-100 text-orange-700 border-orange-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Subscription
                      </button>
                      <button
                        type="button"
                        onClick={() => { setViewMode('details'); setExpandedService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                          viewMode === 'details'
                            ? 'bg-orange-100 text-orange-700 border-orange-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        All Details
                      </button>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {viewMode === 'summary' && `${getServiceSummary(data.unbilled.items).length} service categories`}
                    {viewMode === 'subscription' && `${getSubscriptionSummary(data.unbilled.items).length} subscriptions`}
                    {viewMode === 'details' && `${data.unbilled.items.length} line items`}
                  </span>
                </div>

                {/* Summary by Service View */}
                {viewMode === 'summary' && (
                  <div>
                    {/* Chart and Legend Section */}
                    <div className="px-6 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6 border-b border-gray-200 bg-white">
                      <DonutChart
                        data={getServiceSummary(data.unbilled.items)}
                        total={data.unbilled.totalAmount}
                      />
                      <div className="flex flex-col justify-center">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Top Services</h4>
                        <div className="space-y-2">
                          {getServiceSummary(data.unbilled.items).slice(0, 5).map((service) => (
                            <div key={service.serviceName} className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div
                                  className="h-3 w-3 rounded-full mr-2"
                                  style={{ backgroundColor: getServiceHexColor(service.serviceName) }}
                                ></div>
                                <span className="text-sm text-gray-600">{service.serviceName}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {((service.totalAmount / (data.unbilled?.totalAmount || 1)) * 100).toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Service List */}
                    <div className="divide-y divide-gray-200">
                    {(() => {
                      const services = getServiceSummary(data.unbilled.items)
                      const visibleServices = showAllServices ? services : services.slice(0, MAX_VISIBLE_ITEMS)
                      const hiddenCount = services.length - MAX_VISIBLE_ITEMS

                      return (
                        <>
                          {visibleServices.map((service) => (
                      <div key={service.serviceName}>
                        <div
                          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                          onClick={() => setExpandedService(expandedService === service.serviceName ? null : service.serviceName)}
                        >
                          <div className="flex items-center">
                            <div className={`h-3 w-3 rounded-full ${getServiceColor(service.serviceName)} mr-3`}></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{service.serviceName}</p>
                              <p className="text-xs text-gray-500">
                                {service.itemCount} items • {service.subscriptions.size} subscription{service.subscriptions.size !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm font-semibold text-orange-600 mr-3">
                              {formatCurrency(service.totalAmount)}
                            </span>
                            <span className="text-xs text-gray-400 mr-2">
                              {((service.totalAmount / (data.unbilled?.totalAmount || 1)) * 100).toFixed(1)}%
                            </span>
                            <svg
                              className={`h-4 w-4 text-gray-400 transform transition-transform ${
                                expandedService === service.serviceName ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        {/* Expanded service details */}
                        {expandedService === service.serviceName && (
                          <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Product</th>
                                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Subscription</th>
                                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Quantity</th>
                                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {getItemsByService(data.unbilled?.items || [], service.serviceName).slice(0, 10).map((item, idx) => (
                                  <tr key={idx} className="text-sm">
                                    <td className="py-2 text-gray-900">{item.productName}</td>
                                    <td className="py-2 text-gray-500">{item.subscriptionName}</td>
                                    <td className="py-2 text-gray-900 text-right">{item.quantity.toFixed(3)}</td>
                                    <td className="py-2 text-orange-600 text-right font-medium">{formatCurrency(item.totalAmount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {getItemsByService(data.unbilled?.items || [], service.serviceName).length > 10 && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                Showing top 10 of {getItemsByService(data.unbilled?.items || [], service.serviceName).length} items
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                          ))}
                          {/* Show more button */}
                          {!showAllServices && hiddenCount > 0 && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllServices(true)}
                                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                              >
                                Show {hiddenCount} more service{hiddenCount !== 1 ? 's' : ''}
                              </button>
                            </div>
                          )}
                          {showAllServices && services.length > MAX_VISIBLE_ITEMS && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllServices(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                              >
                                Show less
                              </button>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    </div>
                  </div>
                )}

                {/* Summary by Subscription View */}
                {viewMode === 'subscription' && (
                  <div>
                    {/* Horizontal bar chart for subscriptions */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-white">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">Usage by Subscription</h4>
                      <div className="space-y-3">
                        {getSubscriptionSummary(data.unbilled.items).slice(0, 5).map((sub) => (
                          <div key={sub.subscriptionId}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600 truncate max-w-xs">{sub.subscriptionName}</span>
                              <span className="font-medium text-gray-900">{formatCurrency(sub.totalAmount)}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-orange-500 rounded-full transition-all duration-300"
                                style={{ width: `${(sub.totalAmount / (data.unbilled?.totalAmount || 1)) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Subscription List */}
                    <div className="divide-y divide-gray-200">
                    {(() => {
                      const subscriptions = getSubscriptionSummary(data.unbilled.items)
                      const visibleSubs = showAllSubscriptions ? subscriptions : subscriptions.slice(0, MAX_VISIBLE_ITEMS)
                      const hiddenCount = subscriptions.length - MAX_VISIBLE_ITEMS

                      return (
                        <>
                    {visibleSubs.map((sub) => (
                      <div key={sub.subscriptionId} className="px-6 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{sub.subscriptionName}</p>
                            <p className="text-xs text-gray-500">{sub.itemCount} items</p>
                          </div>
                          <span className="text-sm font-semibold text-orange-600">
                            {formatCurrency(sub.totalAmount)}
                          </span>
                        </div>
                        {/* Service breakdown bar */}
                        <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                          {Object.entries(sub.services)
                            .sort(([, a], [, b]) => b - a)
                            .map(([serviceName, amount], idx) => (
                              <div
                                key={idx}
                                className={`${getServiceColor(serviceName)}`}
                                style={{ width: `${(amount / sub.totalAmount) * 100}%` }}
                                title={`${serviceName}: ${formatCurrency(amount)}`}
                              ></div>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(sub.services)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 4)
                            .map(([serviceName, amount], idx) => (
                              <span key={idx} className="inline-flex items-center text-xs text-gray-600">
                                <span className={`h-2 w-2 rounded-full ${getServiceColor(serviceName)} mr-1`}></span>
                                {serviceName}: {formatCurrency(amount)}
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                          {/* Show more button */}
                          {!showAllSubscriptions && hiddenCount > 0 && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllSubscriptions(true)}
                                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                              >
                                Show {hiddenCount} more subscription{hiddenCount !== 1 ? 's' : ''}
                              </button>
                            </div>
                          )}
                          {showAllSubscriptions && subscriptions.length > MAX_VISIBLE_ITEMS && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllSubscriptions(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                              >
                                Show less
                              </button>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    </div>
                  </div>
                )}

                {/* Detailed View */}
                {viewMode === 'details' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.unbilled.items
                          .sort((a, b) => b.totalAmount - a.totalAmount)
                          .slice(0, 100)
                          .map((item, index) => (
                            <tr key={`${item.subscriptionId}-${index}`} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.subscriptionName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.productName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.chargeStartDate && item.chargeEndDate
                                  ? `${formatDate(item.chargeStartDate)} - ${formatDate(item.chargeEndDate)}`
                                  : 'Current period'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                {item.quantity.toFixed(3)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600 text-right">
                                {formatCurrency(item.totalAmount, item.currency)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {data.unbilled.items.length > 100 && (
                      <div className="px-6 py-3 text-center text-sm text-gray-500 bg-gray-50">
                        Showing top 100 of {data.unbilled.items.length} items (sorted by amount)
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="px-6 py-12 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-2 text-sm">Unbilled usage is not available for individual customers.</p>
                <p className="mt-1 text-xs text-gray-400">View global Azure Billing for current period data.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Billed Usage Section */}
      <div className="bg-white shadow rounded-lg">
        <div
          className="px-4 py-5 sm:px-6 border-b border-gray-200 cursor-pointer hover:bg-gray-50"
          onClick={() => setExpandedSection(expandedSection === 'billed' ? 'unbilled' : 'billed')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-3 w-3 rounded-full bg-green-500 mr-3"></div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">Billed Usage</h3>
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {data?.billed?.items?.length || 0} items
              </span>
              {loadingBilled && (
                <div className="ml-3 inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
              )}
            </div>
            <div className="flex items-center">
              <span className="text-lg font-semibold text-green-600 mr-4">
                {formatCurrency(data?.billed?.totalAmount || 0)}
              </span>
              <svg
                className={`h-5 w-5 text-gray-400 transform transition-transform ${
                  expandedSection === 'billed' ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {data?.billed?.invoiceId
              ? `Invoice ${data.billed.invoiceId}${data.billed.billingPeriodStart && data.billed.billingPeriodEnd ? ` (${formatDate(data.billed.billingPeriodStart)} - ${formatDate(data.billed.billingPeriodEnd)})` : ''}`
              : 'Invoiced Azure usage from previous billing periods.'}
          </p>
        </div>

        {expandedSection === 'billed' && (
          <>
            {/* Invoice Selector */}
            {data?.availableInvoices && data.availableInvoices.length > 0 && (
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <label htmlFor="invoice-select" className="text-sm font-medium text-gray-700">
                    Select Invoice:
                  </label>
                  <select
                    id="invoice-select"
                    value={selectedInvoiceId || data?.billed?.invoiceId || ''}
                    onChange={(e) => loadBilledData(e.target.value)}
                    disabled={loadingBilled}
                    className="block w-80 rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-wait"
                  >
                    {[...data.availableInvoices]
                      .sort((a, b) => new Date(b.invoiceDate || b.billingPeriodEndDate).getTime() - new Date(a.invoiceDate || a.billingPeriodEndDate).getTime())
                      .map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.id} - {formatDate(invoice.invoiceDate)} ({formatCurrency(invoice.totalCharges, invoice.currencyCode)})
                      </option>
                    ))}
                  </select>
                  {loadingBilled && (
                    <span className="text-sm text-gray-500">Loading invoice data...</span>
                  )}
                </div>
              </div>
            )}

            <div>
            {data?.billed?.items && data.billed.items.length > 0 ? (
              <>
                {/* View Mode Toggle */}
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">View by:</span>
                    <div className="inline-flex rounded-md shadow-sm">
                      <button
                        type="button"
                        onClick={() => { setBilledViewMode('summary'); setExpandedBilledService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                          billedViewMode === 'summary'
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Service
                      </button>
                      <button
                        type="button"
                        onClick={() => { setBilledViewMode('subscription'); setExpandedBilledService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium border-t border-b ${
                          billedViewMode === 'subscription'
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Subscription
                      </button>
                      <button
                        type="button"
                        onClick={() => { setBilledViewMode('details'); setExpandedBilledService(null) }}
                        className={`px-3 py-1.5 text-sm font-medium rounded-r-md border ${
                          billedViewMode === 'details'
                            ? 'bg-green-100 text-green-700 border-green-300'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        All Details
                      </button>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500">
                    {billedViewMode === 'summary' && `${getBilledServiceSummary(data.billed.items).length} service categories`}
                    {billedViewMode === 'subscription' && `${getBilledSubscriptionSummary(data.billed.items).length} subscriptions`}
                    {billedViewMode === 'details' && `${data.billed.items.length} line items`}
                  </span>
                </div>

                {/* Summary by Service View */}
                {billedViewMode === 'summary' && (
                  <div>
                    {/* Chart and Legend Section */}
                    <div className="px-6 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6 border-b border-gray-200 bg-white">
                      <DonutChart
                        data={getBilledServiceSummary(data.billed.items)}
                        total={data.billed.totalAmount}
                      />
                      <div className="flex flex-col justify-center">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Top Services</h4>
                        <div className="space-y-2">
                          {getBilledServiceSummary(data.billed.items).slice(0, 5).map((service) => (
                            <div key={service.serviceName} className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div
                                  className="h-3 w-3 rounded-full mr-2"
                                  style={{ backgroundColor: getServiceHexColor(service.serviceName) }}
                                ></div>
                                <span className="text-sm text-gray-600">{service.serviceName}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {((service.totalAmount / (data.billed?.totalAmount || 1)) * 100).toFixed(1)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Service List */}
                    <div className="divide-y divide-gray-200">
                    {(() => {
                      const services = getBilledServiceSummary(data.billed.items)
                      const visibleServices = showAllBilledServices ? services : services.slice(0, MAX_VISIBLE_ITEMS)
                      const hiddenCount = services.length - MAX_VISIBLE_ITEMS

                      return (
                        <>
                          {visibleServices.map((service) => (
                      <div key={service.serviceName}>
                        <div
                          className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                          onClick={() => setExpandedBilledService(expandedBilledService === service.serviceName ? null : service.serviceName)}
                        >
                          <div className="flex items-center">
                            <div className={`h-3 w-3 rounded-full ${getServiceColor(service.serviceName)} mr-3`}></div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{service.serviceName}</p>
                              <p className="text-xs text-gray-500">
                                {service.itemCount} items • {service.subscriptions.size} subscription{service.subscriptions.size !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <span className="text-sm font-semibold text-green-600 mr-3">
                              {formatCurrency(service.totalAmount)}
                            </span>
                            <span className="text-xs text-gray-400 mr-2">
                              {((service.totalAmount / (data.billed?.totalAmount || 1)) * 100).toFixed(1)}%
                            </span>
                            <svg
                              className={`h-4 w-4 text-gray-400 transform transition-transform ${
                                expandedBilledService === service.serviceName ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        {/* Expanded service details */}
                        {expandedBilledService === service.serviceName && (
                          <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                            <table className="min-w-full">
                              <thead>
                                <tr>
                                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Product</th>
                                  <th className="text-left text-xs font-medium text-gray-500 uppercase pb-2">Subscription</th>
                                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Quantity</th>
                                  <th className="text-right text-xs font-medium text-gray-500 uppercase pb-2">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {getBilledItemsByService(data.billed?.items || [], service.serviceName).slice(0, 10).map((item, idx) => (
                                  <tr key={idx} className="text-sm">
                                    <td className="py-2 text-gray-900">{item.productName}</td>
                                    <td className="py-2 text-gray-500">{item.subscriptionName || item.subscriptionId}</td>
                                    <td className="py-2 text-gray-900 text-right">{item.quantity.toFixed(3)}</td>
                                    <td className="py-2 text-green-600 text-right font-medium">{formatCurrency(item.totalAmount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {getBilledItemsByService(data.billed?.items || [], service.serviceName).length > 10 && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                Showing top 10 of {getBilledItemsByService(data.billed?.items || [], service.serviceName).length} items
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                          ))}
                          {/* Show more button */}
                          {!showAllBilledServices && hiddenCount > 0 && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllBilledServices(true)}
                                className="text-sm text-green-600 hover:text-green-700 font-medium"
                              >
                                Show {hiddenCount} more service{hiddenCount !== 1 ? 's' : ''}
                              </button>
                            </div>
                          )}
                          {showAllBilledServices && services.length > MAX_VISIBLE_ITEMS && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllBilledServices(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                              >
                                Show less
                              </button>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    </div>
                  </div>
                )}

                {/* Summary by Subscription View */}
                {billedViewMode === 'subscription' && (
                  <div>
                    {/* Horizontal bar chart for subscriptions */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-white">
                      <h4 className="text-sm font-medium text-gray-700 mb-4">Usage by Subscription</h4>
                      <div className="space-y-3">
                        {getBilledSubscriptionSummary(data.billed.items).slice(0, 5).map((sub) => (
                          <div key={sub.subscriptionId}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600 truncate max-w-xs">{sub.subscriptionName}</span>
                              <span className="font-medium text-gray-900">{formatCurrency(sub.totalAmount)}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full transition-all duration-300"
                                style={{ width: `${(sub.totalAmount / (data.billed?.totalAmount || 1)) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Subscription List */}
                    <div className="divide-y divide-gray-200">
                    {(() => {
                      const subscriptions = getBilledSubscriptionSummary(data.billed.items)
                      const visibleSubs = showAllBilledSubscriptions ? subscriptions : subscriptions.slice(0, MAX_VISIBLE_ITEMS)
                      const hiddenCount = subscriptions.length - MAX_VISIBLE_ITEMS

                      return (
                        <>
                    {visibleSubs.map((sub) => (
                      <div key={sub.subscriptionId} className="px-6 py-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{sub.subscriptionName}</p>
                            <p className="text-xs text-gray-500">{sub.itemCount} items</p>
                          </div>
                          <span className="text-sm font-semibold text-green-600">
                            {formatCurrency(sub.totalAmount)}
                          </span>
                        </div>
                        {/* Service breakdown bar */}
                        <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
                          {Object.entries(sub.services)
                            .sort(([, a], [, b]) => b - a)
                            .map(([serviceName, amount], idx) => (
                              <div
                                key={idx}
                                className={`${getServiceColor(serviceName)}`}
                                style={{ width: `${(amount / sub.totalAmount) * 100}%` }}
                                title={`${serviceName}: ${formatCurrency(amount)}`}
                              ></div>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(sub.services)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 4)
                            .map(([serviceName, amount], idx) => (
                              <span key={idx} className="inline-flex items-center text-xs text-gray-600">
                                <span className={`h-2 w-2 rounded-full ${getServiceColor(serviceName)} mr-1`}></span>
                                {serviceName}: {formatCurrency(amount)}
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                          {/* Show more button */}
                          {!showAllBilledSubscriptions && hiddenCount > 0 && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllBilledSubscriptions(true)}
                                className="text-sm text-green-600 hover:text-green-700 font-medium"
                              >
                                Show {hiddenCount} more subscription{hiddenCount !== 1 ? 's' : ''}
                              </button>
                            </div>
                          )}
                          {showAllBilledSubscriptions && subscriptions.length > MAX_VISIBLE_ITEMS && (
                            <div className="px-6 py-3 text-center border-t border-gray-100">
                              <button
                                onClick={() => setShowAllBilledSubscriptions(false)}
                                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
                              >
                                Show less
                              </button>
                            </div>
                          )}
                        </>
                      )
                    })()}
                    </div>
                  </div>
                )}

                {/* Detailed View */}
                {billedViewMode === 'details' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.billed.items
                          .sort((a, b) => b.totalAmount - a.totalAmount)
                          .slice(0, 100)
                          .map((item, index) => (
                            <tr key={`${item.subscriptionId}-${index}`} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={item.subscriptionName || item.subscriptionId}>
                                {item.subscriptionName || item.subscriptionId}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center">
                                  <div className={`h-2 w-2 rounded-full ${getServiceColor(item.meterCategory || 'Other')} mr-2`}></div>
                                  {item.meterCategory || 'Other'}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={item.productName}>
                                {item.productName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                                {item.quantity.toFixed(3)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                                {formatCurrency(item.totalAmount, item.currency)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    {data.billed.items.length > 100 && (
                      <div className="px-6 py-3 text-center text-sm text-gray-500 bg-gray-50">
                        Showing top 100 of {data.billed.items.length} items (sorted by amount)
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="px-6 py-12 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No billed Azure usage found. Run billing sync to import historical data.</p>
              </div>
            )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
